import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    useEmissionRecordsApiClient,
    useEventFormsApiClient,
    useFormsApiClient,
} from "../../hooks";
import { useCompanyDataForEmission } from "./helpers";

export const useEmissionEventData = (emissionRecordId: string) => {
    const queryClient = useQueryClient();
    // API Clients
    const emissionsApiClient = useEmissionRecordsApiClient();
    const eventFormsApiClient = useEventFormsApiClient();
    const formsApiClient = useFormsApiClient();

    // Emission data
    const emissionRecordQuery = useQuery({
        queryKey: ["emissionRecord", emissionRecordId],
        queryFn: async () => {
            const response = await emissionsApiClient.emissionRecordsRetrieve({
                id: emissionRecordId,
            });
            return response;
        },
        refetchOnWindowFocus: false,
    });

    // Get company settings based on user type.
    const { company } = useCompanyDataForEmission({
        emissionRecord: emissionRecordQuery.data,
    });

    // Retrieve all forms and their field definitions.
    const owner = emissionRecordQuery.data?.owner;
    const availableFormsQuery = useQuery({
        queryKey: ["forms", owner],
        queryFn: async () => {
            const response = await formsApiClient.formsList();
            if (owner) {
                return response.filter(
                    (f) =>
                        !f.owner || f.owner === emissionRecordQuery.data.owner,
                );
            }
            return response;
        },
        placeholderData: [],
        refetchOnMount: "always",
    });

    // Retrieve forms already associated with this emission.
    const eventFormsQuery = useQuery({
        queryKey: ["formsForEvent", emissionRecordQuery.data?.mainEvent.id],
        queryFn: async () => {
            const response = await eventFormsApiClient.eventFormsList({
                event: emissionRecordQuery.data?.mainEvent.id,
            });
            return response;
        },
        enabled: !!emissionRecordQuery.data?.mainEvent,
        placeholderData: [],
    });

    const addFormToEventMutation = useMutation({
        mutationKey: ["addFormToEvent"],
        mutationFn: ({ formId }: { formId: number }) => {
            return eventFormsApiClient.eventFormsCreate({
                eventFormInstanceRequest: {
                    event: emissionRecordQuery.data?.mainEvent.id,
                    form: formId,
                },
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [
                    "formsForEvent",
                    emissionRecordQuery.data?.mainEvent.id,
                ],
            });
            eventFormsQuery.refetch();
        },
    });
    const removeFormFromEventMutation = useMutation({
        mutationKey: ["removeFormFromEvent"],
        mutationFn: ({
            eventFormInstanceId,
        }: {
            eventFormInstanceId: number;
        }) => {
            // We could verify if the user can do this by checking
            // company.defaultForms.includes(
            //     eventFormsQuery.data.find(f => f.id === eventFormInstanceId).form
            // )
            // But let's just leave the backend validate this for us.
            return eventFormsApiClient.eventFormsDestroy({
                id: eventFormInstanceId,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [
                    "formsForEvent",
                    emissionRecordQuery.data?.mainEvent.id,
                ],
            });
            eventFormsQuery.refetch();
        },
    });

    const isLoading =
        emissionRecordQuery.isLoading ||
        availableFormsQuery.isLoading ||
        eventFormsQuery.isLoading;

    return {
        isLoading,
        company,
        addFormToEventMutation,
        removeFormFromEventMutation,
        emissionData: emissionRecordQuery.data,
        availableForms: availableFormsQuery.data,
        eventForms: eventFormsQuery.data,
    };
};
